import { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert,
  CircularProgress, InputAdornment, IconButton
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LangContext';

export function ResetPasswordPage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const zh = lang === 'zh';

  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) {
      setError(zh ? '重置連結無效，請重新申請。' : 'Invalid reset link. Please request a new one.');
    } else {
      setToken(t);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError(zh ? '兩次密碼不一致' : 'Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError(zh ? '密碼至少需要 6 個字元' : 'Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setSuccess(zh ? '密碼已成功重置！即將跳轉至登入頁面...' : 'Password reset successfully! Redirecting to login...');
      setTimeout(() => navigate('/'), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : (zh ? '重置失敗，請重新申請。' : 'Reset failed. Please request a new link.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, #bf360c 0%, #e65100 50%, #ff6d00 100%)',
    }}>
      <Paper sx={{ p: 4, width: '100%', maxWidth: 400, mx: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <img src="/logo.png" alt="PQC Navigator" style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'contain' }} />
          <Typography variant="h5" fontWeight={800} color="primary">PQC Navigator</Typography>
        </Box>

        <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5 }}>
          {zh ? '重置密碼' : 'Reset Password'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {zh ? '請設定您的新密碼' : 'Set your new password below'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>{success}</Alert>}

        {!success && token && (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label={zh ? '新密碼' : 'New Password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              type={showPwd ? 'text' : 'password'}
              required
              inputProps={{ minLength: 6 }}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" color="action" /></InputAdornment>,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPwd(v => !v)}>
                      {showPwd ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              fullWidth
              label={zh ? '確認新密碼' : 'Confirm New Password'}
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              type={showPwd ? 'text' : 'password'}
              required
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><LockOutlinedIcon fontSize="small" color="action" /></InputAdornment>,
              }}
            />
            <Button fullWidth variant="contained" type="submit" disabled={loading} size="large" sx={{ py: 1.5 }}>
              {loading ? <CircularProgress size={22} color="inherit" /> : (zh ? '確認重置密碼' : 'Reset Password')}
            </Button>
          </form>
        )}

        <Button
          fullWidth variant="text" size="small"
          onClick={() => navigate('/')}
          sx={{ mt: 2, color: 'text.secondary' }}
        >
          {zh ? '← 返回登入' : '← Back to Login'}
        </Button>
      </Paper>
    </Box>
  );
}
